import { useState, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  Upload,
  FileText,
  FileSpreadsheet,
  Image as ImageIcon,
  File,
  Trash2,
  Eye,
  Loader2,
  CheckCircle,
  AlertCircle,
  FolderOpen,
  X,
  Sparkles,
  ClipboardPaste,
  Plus
} from 'lucide-react';
import { GlassPanel } from '@/components/layout';
import { cn } from '@/lib/cn';
import { fadeIn, staggerContainer, staggerItem } from '@/lib/motion';
import { useDocumentContextStore, type UploadedDocument } from '@/stores/documentContextStore';
import { 
  getFileType, 
  formatFileSize,
  type SupportedFileType 
} from '@/utils/documentProcessor';
import { api } from '@/lib/api';

/**
 * Documents Tab - Full Document Management
 * 
 * Features:
 * - Drag and drop file upload
 * - PDF, DOCX, TXT, Excel, CSV support
 * - Backend text extraction
 * - Context management for AI conversations
 * - Document preview
 */
type InputMode = 'upload' | 'paste';

export function DocumentsTab() {
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingFile, setProcessingFile] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [previewDoc, setPreviewDoc] = useState<UploadedDocument | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Paste text state
  const [inputMode, setInputMode] = useState<InputMode>('upload');
  const [pasteTitle, setPasteTitle] = useState('');
  const [pasteText, setPasteText] = useState('');

  const { 
    documents, 
    totalCharacters, 
    maxCharacters,
    addDocument, 
    removeDocument, 
    clearAllDocuments,
  } = useDocumentContextStore();

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files);
    await handleFiles(files);
  }, []);

  const handleFileInput = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      await handleFiles(files);
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const handleFiles = async (files: File[]) => {
    setError(null);
    
    for (const file of files) {
      const fileType = getFileType(file.name);
      
      if (!fileType) {
        setError(`Unsupported file: ${file.name}`);
        continue;
      }
      
      // BUG-032: Images now accepted — backend uses Vision OCR to extract text
      // and stores it as document context, just like PDFs and DOCX files.
      
      setIsProcessing(true);
      setProcessingFile(file.name);
      
      try {
        // Upload file to backend for processing
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await api.post('/documents/process', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        
        const result = response.data;
        
        if (!result.success) {
          setError(`Failed to process "${file.name}": ${result.error}`);
          continue;
        }
        
        // Check character limit
        if (totalCharacters + result.character_count > maxCharacters) {
          setError(`Adding "${file.name}" would exceed the ${(maxCharacters / 1000).toFixed(0)}k character limit. Please remove some documents first.`);
          continue;
        }
        
        addDocument({
          id: `${Date.now()}-${file.name}`,
          name: file.name,
          type: fileType as SupportedFileType,
          size: file.size,
          uploadedAt: new Date(),
          extractedText: result.extracted_text,
          pageCount: result.page_count,
        });
        
      } catch (err: any) {
        console.error('Failed to process file:', err);
        const errorMessage = err.response?.data?.detail || err.message || 'Unknown error';
        setError(`Failed to process "${file.name}": ${errorMessage}`);
      }
    }
    
    setIsProcessing(false);
    setProcessingFile(null);
  };

  const handleAddPastedText = useCallback(() => {
    if (!pasteText.trim()) {
      setError('Please enter some text to add');
      return;
    }

    const title = pasteTitle.trim() || `Pasted Text ${new Date().toLocaleTimeString()}`;
    const textContent = pasteText.trim();

    // Check character limit
    if (totalCharacters + textContent.length > maxCharacters) {
      setError(`Adding this text would exceed the ${(maxCharacters / 1000).toFixed(0)}k character limit. Please remove some documents first.`);
      return;
    }

    setError(null);

    addDocument({
      id: `paste-${Date.now()}`,
      name: title,
      type: 'txt',
      size: new Blob([textContent]).size,
      uploadedAt: new Date(),
      extractedText: textContent,
    });

    // Clear form
    setPasteTitle('');
    setPasteText('');
  }, [pasteTitle, pasteText, totalCharacters, maxCharacters, addDocument]);

  const getFileTypeIcon = (type: SupportedFileType) => {
    switch (type) {
      case 'pdf':
      case 'docx':
      case 'txt':
        return <FileText className="w-5 h-5" />;
      case 'xlsx':
      case 'csv':
        return <FileSpreadsheet className="w-5 h-5" />;
      case 'image':
        return <ImageIcon className="w-5 h-5" />;
      default:
        return <File className="w-5 h-5" />;
    }
  };

  const getFileTypeColor = (type: SupportedFileType) => {
    switch (type) {
      case 'pdf':
        return 'text-red-400';
      case 'docx':
      case 'txt':
        return 'text-blue-400';
      case 'xlsx':
      case 'csv':
        return 'text-emerald-400';
      case 'image':
        return 'text-purple-400';
      default:
        return 'text-slate-400';
    }
  };

  return (
    <motion.div
      variants={staggerContainer}
      initial="hidden"
      animate="visible"
      className="space-y-6"
    >
      {/* Header with Context Info */}
      <motion.div variants={staggerItem}>
        <GlassPanel className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-gradient-to-br from-violet-500/20 to-fuchsia-500/20 border border-violet-500/30">
                <FolderOpen className="w-5 h-5 text-violet-400" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-white">Mission Context</h3>
                <p className="text-xs text-slate-400">
                  {documents.length} document{documents.length !== 1 ? 's' : ''} • {(totalCharacters / 1000).toFixed(1)}k characters
                </p>
              </div>
            </div>
            
            {/* Character Usage Bar */}
            <div className="flex items-center gap-3">
              <div className="w-32 h-2 bg-white/10 rounded-full overflow-hidden">
                <div 
                  className={cn(
                    'h-full rounded-full transition-all',
                    totalCharacters > maxCharacters * 0.9 
                      ? 'bg-red-500' 
                      : totalCharacters > maxCharacters * 0.7 
                        ? 'bg-amber-500' 
                        : 'bg-emerald-500'
                  )}
                  style={{ width: `${Math.min(100, (totalCharacters / maxCharacters) * 100)}%` }}
                />
              </div>
              <span className="text-xs text-slate-400 font-mono">
                {Math.round((totalCharacters / maxCharacters) * 100)}%
              </span>
            </div>
          </div>
        </GlassPanel>
      </motion.div>

      {/* Input Mode Toggle */}
      <motion.div variants={staggerItem}>
        <div className="flex justify-center mb-4">
          <div className="inline-flex items-center gap-1 p-1 rounded-xl bg-white/5 border border-white/10">
            <button
              onClick={() => setInputMode('upload')}
              className={cn(
                'flex items-center gap-2 px-4 py-2 rounded-lg transition-all text-sm font-medium',
                inputMode === 'upload'
                  ? 'bg-gradient-to-r from-violet-500/30 to-fuchsia-500/30 text-white border border-violet-500/30'
                  : 'text-white/50 hover:text-white/70'
              )}
            >
              <Upload className="w-4 h-4" />
              Upload File
            </button>
            <button
              onClick={() => setInputMode('paste')}
              className={cn(
                'flex items-center gap-2 px-4 py-2 rounded-lg transition-all text-sm font-medium',
                inputMode === 'paste'
                  ? 'bg-gradient-to-r from-violet-500/30 to-fuchsia-500/30 text-white border border-violet-500/30'
                  : 'text-white/50 hover:text-white/70'
              )}
            >
              <ClipboardPaste className="w-4 h-4" />
              Paste Text
            </button>
          </div>
        </div>
      </motion.div>

      {/* Upload Area */}
      {inputMode === 'upload' && (
        <motion.div variants={staggerItem}>
          <GlassPanel className="p-6">
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => !isProcessing && fileInputRef.current?.click()}
              className={cn(
                'border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer',
                isDragging
                  ? 'border-violet-500 bg-violet-500/10'
                  : isProcessing
                    ? 'border-violet-500/50 bg-violet-500/5'
                    : 'border-white/20 hover:border-violet-500/50 hover:bg-white/5'
              )}
            >
              <input
                type="file"
                ref={fileInputRef}
                multiple
                accept=".pdf,.docx,.doc,.txt,.xlsx,.xls,.csv"
                onChange={handleFileInput}
                className="hidden"
              />

              {isProcessing ? (
                <div className="flex flex-col items-center gap-3">
                  <Loader2 className="w-12 h-12 text-violet-400 animate-spin" />
                  <p className="text-white font-medium">
                    Processing {processingFile}...
                  </p>
                  <p className="text-sm text-slate-400">
                    Extracting text on server
                  </p>
                </div>
              ) : (
                <>
                  <Upload className={cn(
                    'w-12 h-12 mx-auto mb-4 transition-colors',
                    isDragging ? 'text-violet-400' : 'text-white/40'
                  )} />
                  <h3 className="text-lg font-medium text-white mb-2">
                    Drop documents here
                  </h3>
                  <p className="text-sm text-white/50 mb-4">
                    PDF, DOCX, TXT, Excel, CSV
                  </p>
                  <div className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-violet-500/20 text-violet-300 hover:bg-violet-500/30 transition-colors">
                    <FileText className="w-4 h-4" />
                    Browse Files
                  </div>
                </>
              )}
            </div>
          </GlassPanel>
        </motion.div>
      )}

      {/* Paste Text Area */}
      {inputMode === 'paste' && (
        <motion.div
          variants={staggerItem}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <GlassPanel className="p-6">
            <div className="space-y-4">
              {/* Title Input */}
              <div>
                <label className="block text-sm text-white/60 mb-2">
                  Title (optional)
                </label>
                <input
                  type="text"
                  value={pasteTitle}
                  onChange={(e) => setPasteTitle(e.target.value)}
                  placeholder="e.g., Charter Party Details, Cargo Specs..."
                  className={cn(
                    'w-full px-4 py-3 rounded-xl',
                    'bg-white/5 border border-white/10',
                    'text-white placeholder-white/30',
                    'focus:outline-none focus:ring-2 focus:ring-violet-500/50'
                  )}
                />
              </div>

              {/* Text Area */}
              <div>
                <label className="block text-sm text-white/60 mb-2">
                  Paste your text content
                </label>
                <textarea
                  value={pasteText}
                  onChange={(e) => setPasteText(e.target.value)}
                  placeholder="Paste contract details, specifications, notes, or any text you want the AI to reference during your conversation..."
                  rows={8}
                  className={cn(
                    'w-full px-4 py-3 rounded-xl resize-none',
                    'bg-white/5 border border-white/10',
                    'text-white placeholder-white/30',
                    'focus:outline-none focus:ring-2 focus:ring-violet-500/50'
                  )}
                />
                <div className="flex justify-between mt-2">
                  <p className="text-xs text-white/40">
                    {pasteText.length > 0 && `${(pasteText.length / 1000).toFixed(1)}k characters`}
                  </p>
                  <p className="text-xs text-white/40">
                    {pasteText.length > 0 && `Remaining: ${((maxCharacters - totalCharacters - pasteText.length) / 1000).toFixed(1)}k`}
                  </p>
                </div>
              </div>

              {/* Add Button */}
              <button
                onClick={handleAddPastedText}
                disabled={!pasteText.trim()}
                className={cn(
                  'w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-medium transition-all',
                  pasteText.trim()
                    ? 'bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white hover:from-violet-400 hover:to-fuchsia-400'
                    : 'bg-white/5 text-white/30 cursor-not-allowed'
                )}
              >
                <Plus className="w-5 h-5" />
                Add to Context
              </button>
            </div>
          </GlassPanel>
        </motion.div>
      )}

      {/* Error Display */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <GlassPanel className="p-4 border-red-500/30">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
              <p className="text-sm text-red-300 flex-1">{error}</p>
              <button
                onClick={() => setError(null)}
                className="p-1 rounded hover:bg-white/10"
              >
                <X className="w-4 h-4 text-red-400" />
              </button>
            </div>
          </GlassPanel>
        </motion.div>
      )}

      {/* Document List */}
      {documents.length > 0 && (
        <motion.div variants={staggerItem}>
          <GlassPanel className="p-4">
            <div className="flex justify-between items-center mb-4 px-2">
              <h3 className="text-sm font-medium text-white/70">
                Uploaded Documents ({documents.length})
              </h3>
              <button
                onClick={clearAllDocuments}
                className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1"
              >
                <Trash2 className="w-3 h-3" />
                Clear All
              </button>
            </div>
            <div className="space-y-2">
              {documents.map((doc) => (
                <motion.div
                  key={doc.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="flex items-center gap-3 p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors group"
                >
                  <div className={cn('flex-shrink-0', getFileTypeColor(doc.type))}>
                    {getFileTypeIcon(doc.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate">{doc.name}</p>
                    <p className="text-xs text-white/50">
                      {formatFileSize(doc.size)}
                      {doc.pageCount && ` • ${doc.pageCount} pages`}
                      {doc.extractedText
                        ? ` • ${(doc.extractedText.length / 1000).toFixed(1)}k chars`
                        : ' • re-upload needed'}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    {doc.extractedText ? (
                      <CheckCircle className="w-4 h-4 text-emerald-400" />
                    ) : (
                      <span className="text-xs text-amber-400 font-medium">reload</span>
                    )}
                    <button
                      onClick={() => setPreviewDoc(doc)}
                      className="p-2 rounded-lg hover:bg-white/10 transition-colors opacity-0 group-hover:opacity-100"
                      title="Preview"
                    >
                      <Eye className="w-4 h-4 text-violet-400" />
                    </button>
                    <button
                      onClick={() => removeDocument(doc.id)}
                      className="p-2 rounded-lg hover:bg-white/10 transition-colors opacity-0 group-hover:opacity-100"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          </GlassPanel>
        </motion.div>
      )}

      {/* How It Works */}
      <motion.div variants={staggerItem}>
        <GlassPanel className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <Sparkles className="w-5 h-5 text-violet-400" />
            <h3 className="text-sm font-bold text-white">How It Works</h3>
          </div>
          <div className="space-y-3 text-sm text-slate-400">
            <p>
              <span className="text-white font-medium">1. Add context</span> - Upload files (PDF, DOCX, Excel) or paste text directly.
            </p>
            <p>
              <span className="text-white font-medium">2. Text is processed</span> - Content is extracted and indexed for the AI.
            </p>
            <p>
              <span className="text-white font-medium">3. Ask questions</span> - The AI uses your documents as context during voice conversations.
            </p>
          </div>
        </GlassPanel>
      </motion.div>

      {/* Empty State */}
      {documents.length === 0 && !isProcessing && (
        <motion.div variants={fadeIn} className="text-center py-8">
          <p className="text-white/40 text-sm">
            Upload documents or paste text to provide context for the AI.
            This could be charter parties, cargo specs, contract details, or any relevant information.
          </p>
        </motion.div>
      )}

      {/* Document Preview Modal */}
      {previewDoc && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setPreviewDoc(null)}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-slate-900 w-full max-w-3xl max-h-[80vh] rounded-2xl shadow-2xl border border-white/10 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-white/10 flex justify-between items-center">
              <div className="flex items-center gap-3">
                <div className={getFileTypeColor(previewDoc.type)}>
                  {getFileTypeIcon(previewDoc.type)}
                </div>
                <div>
                  <h3 className="text-white font-medium">{previewDoc.name}</h3>
                  <p className="text-xs text-slate-400">
                    {formatFileSize(previewDoc.size)} • {((previewDoc.extractedText?.length || 0) / 1000).toFixed(1)}k characters
                  </p>
                </div>
              </div>
              <button
                onClick={() => setPreviewDoc(null)}
                className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/10"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 max-h-[60vh] overflow-y-auto">
              <pre className="text-sm text-slate-300 whitespace-pre-wrap font-mono">
                {previewDoc.extractedText || 'No text content extracted.'}
              </pre>
            </div>
          </motion.div>
        </motion.div>
      )}
    </motion.div>
  );
}

export default DocumentsTab;
